import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../shared/InfoTip';
import { uploadImport } from '../../api/exports';
import { useAuthStore } from '../../stores/authStore';
import { hasMinRole } from '../../utils/rbac';
import { toast } from '../../stores/uiStore';
import styles from './ImportPage.module.css';

type ImportFormat = 'csv' | 'xlsx' | 'geojson' | 'shapefile';

interface FormatOption {
  key: ImportFormat;
  name: string;
  ext: string;
  accept: string;
  descKey: string;
}

const FORMATS: FormatOption[] = [
  { key: 'csv', name: 'CSV', ext: '.csv', accept: '.csv,text/csv', descKey: 'csvDesc' },
  { key: 'xlsx', name: 'Excel', ext: '.xlsx', accept: '.xlsx,.xls', descKey: 'xlsxDesc' },
  { key: 'geojson', name: 'GeoJSON', ext: '.geojson', accept: '.geojson,.json', descKey: 'geojsonDesc' },
  { key: 'shapefile', name: 'Shapefile', ext: '.zip', accept: '.zip', descKey: 'shapefileDesc' },
];

export function ImportPage() {
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation('import');
  const [format, setFormat] = useState<ImportFormat>('csv');
  const [file, setFile] = useState<File | null>(null);
  const [targetTable, setTargetTable] = useState('grands_projets');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isAdmin = user ? hasMinRole(user.role, 'tenant_admin') : false;
  const activeFormat = FORMATS.find((f) => f.key === format)!;

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setResult(null);
      setError(null);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setResult(null);
      setError(null);
    }
  }, []);

  async function handleUpload() {
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      setResult(null);
      const data = await uploadImport(format, file, format === 'csv' ? targetTable : undefined);
      setResult(data);
      toast.success(t('importSuccess', { count: data.rows_parsed ?? data.features_parsed ?? 'N/A' }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('importFailed');
      setError(msg);
      toast.error(t('importFailed'));
    } finally {
      setUploading(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <h2 className={styles.title}>{t('title')}</h2>
        <div className={styles.noAccess}>
          {t('noAccess')}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>{t('title')}<InfoTip text={t('tooltips.format')} /></h2>

      {/* Format selector */}
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{t('format')}</span>
        <div className={styles.formatGrid}>
          {FORMATS.map((f) => (
            <button
              key={f.key}
              className={`${styles.formatCard} ${format === f.key ? styles.formatActive : ''}`}
              onClick={() => { setFormat(f.key); setFile(null); setResult(null); setError(null); }}
            >
              <div className={styles.formatName}>{f.name}</div>
              <div className={styles.formatExt}>{f.ext}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Format description */}
      <div className={styles.descCard}>
        <div className={styles.descTitle}>{t('importLabel', { name: activeFormat.name })}</div>
        <div className={styles.descText}>{t(activeFormat.descKey)}</div>
      </div>

      {/* Target table (CSV only) */}
      {format === 'csv' && (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('targetTable')}</span>
          <select
            className={styles.select}
            value={targetTable}
            onChange={(e) => setTargetTable(e.target.value)}
          >
            <option value="grands_projets">{t('projectsTable')}</option>
            <option value="plq_entries">{t('plqEntries')}</option>
            <option value="organizations">{t('organizationsTable')}</option>
          </select>
        </div>
      )}

      {/* File drop zone */}
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{t('file')}</span>
        <div
          className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept={activeFormat.accept}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {file ? (
            <div className={styles.fileName}>
              <span>{file.name}</span>
              <span className={styles.fileSize}>
                {(file.size / 1024).toFixed(1)} KB
              </span>
            </div>
          ) : (
            <div className={styles.dropPrompt}>
              {t('dropPrompt', { ext: activeFormat.ext })}
            </div>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Upload button */}
      <button
        className={styles.uploadBtn}
        onClick={handleUpload}
        disabled={!file || uploading}
      >
        {uploading ? t('uploading') : t('uploadAndParse')}
      </button>

      {/* Results preview */}
      {result && (
        <div className={styles.resultPanel}>
          <h3 className={styles.resultTitle}>{t('importResults')}</h3>
          <div className={styles.resultStats}>
            {result.rows_parsed != null && (
              <div className={styles.statItem}>
                <span className={styles.statValue}>{result.rows_parsed as number}</span>
                <span className={styles.statLabel}>{t('rowsParsed')}</span>
              </div>
            )}
            {result.features_parsed != null && (
              <div className={styles.statItem}>
                <span className={styles.statValue}>{result.features_parsed as number}</span>
                <span className={styles.statLabel}>{t('featuresParsed')}</span>
              </div>
            )}
            {result.sheets != null && (
              <div className={styles.statItem}>
                <span className={styles.statValue}>{Object.keys(result.sheets as object).length}</span>
                <span className={styles.statLabel}>{t('sheets')}</span>
              </div>
            )}
          </div>

          {/* CSV/GeoJSON preview */}
          {Array.isArray(result.preview) && (result.preview as Record<string, unknown>[]).length > 0 && (
            <div className={styles.previewTable}>
              <div className={styles.previewLabel}>{t('previewRows', { count: (result.preview as unknown[]).length })}</div>
              <div className={styles.tableWrap}>
                <table>
                  <thead>
                    <tr>
                      {Object.keys((result.preview as Record<string, unknown>[])[0]!).map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(result.preview as Record<string, unknown>[]).map((row, i) => (
                      <tr key={i}>
                        {Object.values(row).map((val, j) => (
                          <td key={j}>{val == null ? '\u2014' : String(val).slice(0, 50)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* XLSX sheets preview */}
          {result.sheets != null && Object.entries(result.sheets as Record<string, { rows: number; preview: Record<string, unknown>[] }>).map(([name, sheet]) => (
            <div key={name} className={styles.previewTable}>
              <div className={styles.previewLabel}>{t('sheetLabel', { name, rows: sheet.rows })}</div>
              {sheet.preview.length > 0 && (
                <div className={styles.tableWrap}>
                  <table>
                    <thead>
                      <tr>
                        {Object.keys(sheet.preview[0]!).map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sheet.preview.map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).map((val, j) => (
                            <td key={j}>{val == null ? '\u2014' : String(val).slice(0, 50)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
